/**
 * Canal-wall fittings (lifted from lantern-water's `quay-masonry.js`
 * `quayFittings`): on both quay faces (x 4 facing east, x 10.5 facing west),
 * three coursed buttresses rising to under the terrace or street coping, four
 * open stone scupper gutters projecting from dark inlets, and five mooring
 * stones with iron rings near the waterline. Placed at the origin (the quay
 * faces are the town's). No params.
 */
import * as THREE from 'three';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'quay_fittings',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    root.name = 'canal-scupper-and-mooring-stones';
    const { box, mesh, m, kit } = ctx;
    const ring = kit.shared('quay-fittings:ring', () => new THREE.TorusGeometry(0.13, 0.024, 6, 16));
    for (const [x, side] of [
      [4, 1],
      [10.5, -1],
    ] as const) {
      const terraceTop = side === 1 ? 2.4 : 3.36;
      for (const z of [-16.8, -8.25, -5.15]) {
        const p = new THREE.Group();
        p.position.set(x, 0, z);
        p.rotation.y = (side * Math.PI) / 2;
        root.add(p);
        const bottom = -1.3;
        const top = terraceTop - 0.24;
        const courses = Math.ceil((top - bottom) / 0.38);
        const height = (top - bottom) / courses;
        for (let i = 0; i < courses; i += 1) {
          const projection = 0.25 + (1 - i / courses) * 0.14;
          box(p, 0, bottom + (i + 0.5) * height, projection / 2 - 0.025, 0.55, height - 0.012, projection, m.stone);
        }
        box(p, 0, terraceTop - 0.235, 0.15, 0.66, 0.1, 0.4, m.stone);
        box(p, 0, terraceTop - 0.63, 0.13, 0.63, 0.11, 0.38, m.limestone);
      }
      for (const z of [-15.2, -6.8, 5.2, 15.6]) {
        const top = z < -4.8 ? (side === 1 ? 2.4 : 3.36) : 0;
        const p = new THREE.Group();
        p.position.set(x, top - 0.36, z);
        p.rotation.y = (side * Math.PI) / 2;
        root.add(p);
        // An open stone gutter projects from a dark inlet in the wall.
        box(p, 0, 0, 0.018, 0.26, 0.2, 0.025, m.dark);
        for (const edge of [-1, 1]) box(p, edge * 0.17, 0, 0.14, 0.08, 0.25, 0.3, m.stone);
        box(p, 0, -0.115, 0.14, 0.4, 0.06, 0.3, m.stone);
        box(p, 0, 0.145, 0.07, 0.46, 0.065, 0.18, m.cream);
      }
      for (const z of [-13.7, -8.3, 3.7, 7.2, 14.2]) {
        const p = new THREE.Group();
        p.position.set(x, z < -4.8 ? 0.62 : -0.23, z);
        p.rotation.y = (side * Math.PI) / 2;
        root.add(p);
        box(p, 0, 0.06, 0.035, 0.26, 0.25, 0.07, m.stone);
        box(p, 0, 0.065, 0.085, 0.085, 0.085, 0.04, m.iron);
        mesh(p, ring, m.iron, 0, -0.05, 0.13);
      }
    }
    return { root };
  },
};
